import { catchErrorTyped } from '@/utils/helpers/http';

export interface ChangeGroup {
  category: string;
  items: string[];
}

export interface VersionData {
  version: string;
  date: string;
  silent: boolean;
  changes: ChangeGroup[];
}

const VERSION_KEY = 'app-version';
const SHOW_CHANGELOG_KEY = 'app-show-changelog';
const CHANGELOG_DATA_KEY = 'app-changelog-data';

export const useVersion = () => {
  /**
   * Fetches version.json and compares the build timestamp to the locally stored
   * one. Returns VersionData (from the changelog field) when a newer build is
   * available, false otherwise.
   *
   * version.json shape:
   *   { version: "<timestamp>", changelog: VersionData | null }
   */
  const checkVersion = async (): Promise<VersionData | false> => {
    const [, resp] = await catchErrorTyped(fetch('/version.json', { cache: 'no-store' }));

    if (!resp) return false;

    const [, data] = await catchErrorTyped(resp.json());

    // `version` is always the build timestamp used for mismatch detection
    if (!data?.version) return false;

    const current = localStorage.getItem(VERSION_KEY);

    // First visit: store the current build timestamp and do nothing else.
    if (!current) {
      localStorage.setItem(VERSION_KEY, data.version);
      return false;
    }

    if (current !== data.version) {
      // Return the parsed changelog entry so callers can display it.
      // Fall back to a minimal object if CHANGELOG.md had no entry.
      const changelog: VersionData = data.changelog ?? {
        version: data.version,
        date: '',
        silent: false,
        changes: [],
      };
      return changelog;
    }

    return false;
  };

  /**
   * Clears caches, updates the service worker, persists the new build timestamp,
   * and reloads the page. If the release is not silent, stores changelog data so
   * the modal is shown after reload.
   */
  const performUpdate = async (versionData: VersionData): Promise<void> => {
    console.log(`[Update] 🔄 New build detected – changelog version: ${versionData.version}`);
    if (versionData.changes?.length) {
      console.log('[Update] 📋 Changes in this release:');
      for (const group of versionData.changes) {
        console.log(`  [${group.category}]`);
        for (const item of group.items) {
          console.log(`    • ${item}`);
        }
      }
    }

    // Re-fetch the current build timestamp so we persist the right value
    try {
      const resp = await fetch('/version.json', { cache: 'no-store' });
      const data = await resp.json();
      if (data?.version) {
        localStorage.setItem(VERSION_KEY, data.version);
        console.log('[Update] 💾 Build timestamp saved:', data.version);
      }
    } catch (e) {
      console.warn('[Update] ⚠️ Could not re-fetch version.json:', e);
    }

    if (!versionData.silent) {
      try {
        localStorage.setItem(SHOW_CHANGELOG_KEY, 'true');
        localStorage.setItem(CHANGELOG_DATA_KEY, JSON.stringify(versionData));
        console.log('[Update] 📝 Changelog will be shown after reload');
      } catch (e) {
        console.warn('[Update] ⚠️ localStorage write failed:', e);
      }
    } else {
      console.log('[Update] 🔇 Silent release – changelog modal suppressed');
    }

    // Clear all SW caches so the new build assets are served fresh
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((n) => caches.delete(n)));
        console.log(`[Update] 🗑️  Cleared ${cacheNames.length} cache(s)`);
      } catch (e) {
        console.warn('[Update] ⚠️ Cache clear failed:', e);
      }
    }

    // Trigger a SW update so the new worker is ready immediately
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          await registration.update();
          console.log('[Update] ⚙️  Service worker updated');
        }
      } catch (e) {
        console.warn('[Update] ⚠️ Service worker update failed:', e);
      }
    }

    console.log('[Update] 🚀 Reloading page…');
    window.location.reload();
  };

  /**
   * Call once on app mount. Returns pending VersionData if the app just updated
   * and a changelog should be displayed; otherwise returns null.
   * Clears the pending flags after reading.
   */
  const getPendingChangelog = (): VersionData | null => {
    const shouldShow = localStorage.getItem(SHOW_CHANGELOG_KEY);
    if (!shouldShow) return null;

    try {
      const raw = localStorage.getItem(CHANGELOG_DATA_KEY);
      localStorage.removeItem(SHOW_CHANGELOG_KEY);
      localStorage.removeItem(CHANGELOG_DATA_KEY);
      return raw ? (JSON.parse(raw) as VersionData) : null;
    } catch {
      return null;
    }
  };

  return {
    checkVersion,
    performUpdate,
    getPendingChangelog,
  };
};
